import { PROJECTS_DEFAULT_QUERY_PARAMS, UserDisplayName } from "@/components";
import DashboardTablePreview from "@/components/Dashboard/DashboardTablePreview";
import { SmallerNameCol } from "@/components/ProjectsTable/styled";
import { recentlyApprovedMilestoneFieldNames as fieldNames } from "@/enums";
import { routes } from "@/helpers";
import { useColumnFactory } from "@/hooks";
import {
  useGetRecentlyApprovedMilestonesQuery,
  useGetUsersByIdsMutation,
} from "@/services";
import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "antd";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { DateParam, useQueryParams } from "use-query-params";

const RecentlyApprovedMilestonesPreview = () => {
  const intl = useIntl();
  const [queryParams, setQueryParams] = useQueryParams({
    ...PROJECTS_DEFAULT_QUERY_PARAMS,
    estimatedLaunchDate: DateParam,
  });

  const { createMilestoneViewLinkColumn } = useColumnFactory(
    queryParams,
    setQueryParams
  );

  const {
    data: { data: projectRecentlyApprovedMilestones, totalCount } = {},
    isFetching,
  } = useGetRecentlyApprovedMilestonesQuery({ ...queryParams, pageSize: 5 });

  const [getUsersByIds, { data: usersByIds }] = useGetUsersByIdsMutation();

  useEffect(() => {
    const ids = projectRecentlyApprovedMilestones?.map(
      ({ approver }) => approver
    );
    if (ids?.length) {
      const uniqueApproversIds = [...new Set(ids)];
      getUsersByIds(uniqueApproversIds);
    }
  }, [projectRecentlyApprovedMilestones, getUsersByIds]);

  const getApproverDisplay = (userId) => {
    if (!userId) return null;

    const userData = usersByIds?.find(({ id }) => id === userId);

    if (!userData) return null;

    return (
      <UserDisplayName
        displayName={userData?.displayName}
        userAccountEnabled={userData?.azureAdAccountEnabled}
        user={userData}
      />
    );
  };

  return (
    <DashboardTablePreview
      data={projectRecentlyApprovedMilestones}
      isFetching={isFetching}
      title={intl.formatMessage({
        id: "dashboard.titles.recentlyApprovedMilestones",
      })}
      titleRoute={routes.dashboardFullViews.recentlyApprovedMilestones}
      totalCount={totalCount}
      columns={[
        createMilestoneViewLinkColumn(
          intl.formatMessage({ id: "dashboard.columns.milestone" }),
          intl.formatMessage({ id: "dashboard.columns.view" })
        ),
        {
          title: intl.formatMessage({ id: "dashboard.columns.projectNumber" }),
          dataIndex: fieldNames.projectNumber,
        },
        {
          title: intl.formatMessage({ id: "dashboard.columns.projectName" }),
          render: ({ projectName, secureProject }) => (
            <SmallerNameCol>
              {secureProject && (
                <Tooltip
                  title={intl.formatMessage({ id: "project.isSecureMessage" })}
                  trigger={["hover"]}
                >
                  <FontAwesomeIcon icon={faLock} className="mr-2" />
                </Tooltip>
              )}
              {projectName}
            </SmallerNameCol>
          ),
        },
        {
          title: intl.formatMessage({ id: "dashboard.columns.milestone" }),
          dataIndex: fieldNames.milestoneName,
        },
        {
          title: intl.formatMessage({ id: "dashboard.columns.approver" }),
          render: (milestone) => getApproverDisplay(milestone.approver),
        },
        {
          title: intl.formatMessage({ id: "dashboard.columns.schedule" }),
          dataIndex: fieldNames.scheduleName,
        },
        {
          title: intl.formatMessage({ id: "dashboard.columns.days" }),
          dataIndex: fieldNames.daysAvailable,
        },
      ]}
    />
  );
};

export default RecentlyApprovedMilestonesPreview;
